import React, { useEffect, useState } from "react";
import { useRouter } from "@src/lib/router";
import { getAwsAssets } from "@src/utils";
import Image from "@src/lib/Image";
import styles from "./QrPayment.module.scss";
import timerIcon from "@src/assets/timer.svg";
import usePolling from "@src/customHooks/usePolling";
import apiEndPoints from "@src/PaymentService/apiEndPoints";
import { CONSTANTS, PAYMENT_STATUS, QR_PAYMENT_TIMEOUT } from "@src/PaymentService/constants";
import { paymentBlurDataImage } from "@src/utils/constants";
import getAxiosConfig from "@src/utils/getAxiosConfig";
import { useSelector } from "react-redux";
import { safeJsonParse } from "@src/utils/safeJsonParse";

function PaymentHeader({
  orgName = "Prateek Classes",
  orgLogo = getAwsAssets(`shareIcon.svg`, "layout"),
  handleBack = () => {}
}) {
  return (
    <div className={styles.paymentHeader}>
      <div className={styles.left}>
        <div
          className={styles.back}
          onClick={() => handleBack()}
        >
          <Image
            src={getAwsAssets(`leftArrowIcon.svg`, "layout")}
            alt="leftArrowIcon"
            height={16}
            width={16}
            layout="fixed"
            priority={true}
            loading="eager"
          />
        </div>
        <picture className={styles.logo}>
          <Image
            src={orgLogo}
            alt="shareIcon"
            height={22}
            width={22}
            layout="fixed"
            priority={true}
            loading="eager"
          />
        </picture>
        <h1>{orgName.length > 20 ? `${orgName.substring(0, 20)}...` : orgName}</h1>
      </div>
    </div>
  );
}

function QrPayment({ imageUrl, gatewayOrderId }) {
  const router = useRouter();
  const config = getAxiosConfig(apiEndPoints[CONSTANTS.GET_QR_PAYMENT_STATUS_RZP].method, apiEndPoints[CONSTANTS.GET_QR_PAYMENT_STATUS_RZP].url, gatewayOrderId)
  const orgName = useSelector((state)=> state?.overviewReducer?.overViewDetails?.course?.orgDetails?.name)
  const appIcon = useSelector((state) => state?.persistedDataReducer?.orgDetails?.organization?.appIcon)
  const { data, error } = usePolling(config, 3000);
  const [isLoading, setIsLoading] = useState(true)

  const [timeLeft, setTimeLeft] = useState(QR_PAYMENT_TIMEOUT);
  const handleBack = () => {
    router.back();
  };

  useEffect(() => {
    const handlePopState = (event) => {
      // Trigger the function when back navigation is attempted
      const status = safeJsonParse(sessionStorage.getItem("status"), {
        source: "QrPayment [sessionStorage.status]",
        fallback: 2,
      }) || 2
      sessionStorage.removeItem("status")
      window.onPaymentUpdated(JSON.stringify({ status}));
    };

    // Listen for the popstate event (when back navigation happens)
    window.addEventListener("popstate", handlePopState);

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  useEffect(() => {
    if(isLoading) return
    if (timeLeft <= 0) {
      handleBack(2)
      return;
    }
  }, [timeLeft, isLoading]);
  
  useEffect(() => {
    if(isLoading) return
    let intervalId;
    let backgroundStartTime;
    let currentTimeLeft = QR_PAYMENT_TIMEOUT
    const updateCounter = () => {
      currentTimeLeft = currentTimeLeft - 1
      if(currentTimeLeft < 0) currentTimeLeft = 0
      setTimeLeft(currentTimeLeft);
    };

    // Start the counter interval
    intervalId = setInterval(updateCounter, 1000);

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // When the page goes to background
        backgroundStartTime = performance.now(); // Track time when backgrounded
        clearInterval(intervalId); // Pause counter when in background
      } else {
        // When the page returns to foreground
        const backgroundElapsedTime = Math.ceil((performance.now() - backgroundStartTime) / 1000); // Calculate background time in seconds
        currentTimeLeft = currentTimeLeft - backgroundElapsedTime
        intervalId = setInterval(updateCounter, 1000); // Resume counter when back in foreground
      }
    };

    // Listen for visibility changes (when app goes to background or comes back)
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearInterval(intervalId); // Cleanup interval on unmount
    };
  }, [isLoading]);

  useEffect(() => {
    if(error){
      handleBack(2)
    } else if(data?.data?.status == PAYMENT_STATUS.paid){
      sessionStorage.setItem("status", 1)
      handleBack(1)
    }
  }, [data, error])
  

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
  };

  useEffect(() => {
    if (typeof window != "undefined" && router.isReady && !imageUrl) {
      router.push("/");
    }
  }, [imageUrl, router]);

  if (!router.isReady) {
    return null;
  }

  return (
    <section className={styles.paymentContainer}>
      <PaymentHeader handleBack={handleBack} orgName={orgName} orgLogo={appIcon} />
      <div className={styles.qrImageContainer}>
        <div className={styles.qrImage}>
        {isLoading && (
        <img
          src={paymentBlurDataImage}
          alt={"Qr Placeholder image"}
          width={"100%"}
          height={"100%"}
          style={{
            position: "absolute",
            filter: "blur(20px)",
            transition: "opacity 0.5s ease-in-out",
            objectFit: "cover",
            width: "100%",
            height: "100%",
          }}
        />)}
        <img
            src={imageUrl}
            alt="qr code image"
            className={styles.img}
            onLoad={()=> {
              setIsLoading(false)
            }}
          />
        </div>
      </div>
      <div className={styles.timer}>
      </div>
      {!isLoading ? <div className={styles.timerContainer}>
        <picture className={styles.timerImg}>
          <Image
            src={timerIcon}
            alt="shareIcon"
            layout="fill"
            objectFit="contain"
            priority
          />
        </picture>
        <p>{formatTime(timeLeft)}</p>
      </div> : null}
    </section>
  );
}

export default QrPayment;
